import React, { useCallback, useEffect, useRef, useState } from 'react';
import classnames from 'classnames';
import { useTheme } from '../theme';
import {
  IconCheckmarkCircle,
  IconExclamationCircle,
  IconInformationSolid,
  IconWarningSign,
} from '../icons';
import { ToastSettings } from './types';
import { useToastContext } from './toast-context-provider';

export const variants = ['default', 'informative', 'warning', 'success', 'error'] as const;
export type ToastVariant = typeof variants[number];

const iconVariantMapping: Record<
  ToastVariant,
  ((props: React.SVGProps<SVGSVGElement>) => React.ReactElement) | null
> = {
  default: null,
  informative: IconInformationSolid,
  success: IconCheckmarkCircle,
  error: IconExclamationCircle,
  warning: IconWarningSign,
};

export type ToastProps = { id: string } & ToastSettings & React.HTMLAttributes<HTMLDivElement>;

export const Toast = (props: ToastProps): React.ReactElement => {
  const [, removeToast] = useToastContext();
  const [isExiting, setIsExiting] = useState(false);
  const exitTimeout = useRef<number | null>(null);
  const theme = useTheme();

  const { id, variant, hasIcon, autoClose, closeOnClick, className, children, ...rest } = props;

  const classNames = classnames(
    className,
    'airtame-toast__item',
    `airtame-toast__item--${variant}`,
    {
      [`airtame-toast__item--exiting`]: isExiting,
      [`airtame-toast__item--clickable`]: closeOnClick,
    }
  );

  const Icon = iconVariantMapping[variant];
  const shouldDisplayIcon = hasIcon && !!Icon;

  const clearExitTimeout = useCallback(() => {
    if (exitTimeout.current) {
      clearTimeout(exitTimeout.current);
    }
  }, []);

  const startAutoClose = () => {
    exitTimeout.current = window.setTimeout(() => {
      setIsExiting(true);
    }, autoClose);
  };

  const handleAnimationEnd = (event: React.AnimationEvent<HTMLDivElement>) => {
    if (event.animationName === 'slide-in') {
      startAutoClose();
    }

    if (event.animationName === 'collapse') {
      removeToast(id);
    }
  };

  const handleClick = () => {
    if (closeOnClick) {
      setIsExiting(true);

      clearExitTimeout();
    }
  };

  useEffect(() => {
    return clearExitTimeout;
  }, [clearExitTimeout]);

  return (
    <div
      onClick={handleClick}
      className={classNames}
      onAnimationEnd={handleAnimationEnd}
      data-theme={theme}
      {...rest}
    >
      {shouldDisplayIcon && <Icon className="airtame-toast__icon" />}
      {children}
    </div>
  );
};
